import React, { useCallback, useEffect, useState } from "react";
import {
    CheckCircle2, XCircle, RefreshCw, Loader2, Building2, Eye,
    MapPin, DollarSign, Briefcase,
} from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";

const REJECT_MIN_LEN = 5;

export default function JobModerationQueue() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);

    // Modals: preview + reject-with-reason
    const [previewId, setPreviewId] = useState(null);
    const [previewJob, setPreviewJob] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [rejectId, setRejectId] = useState(null);
    const [rejectReason, setRejectReason] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/jobs/pending");
            setRows(data.results || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const openPreview = async (job) => {
        setPreviewId(job.id);
        setPreviewLoading(true);
        setPreviewJob(null);
        try {
            const { data } = await api.get(`/admin/jobs/${job.id}/preview`);
            setPreviewJob(data);
        } catch (e) {
            toast.error(formatApiError(e));
            setPreviewId(null);
        } finally { setPreviewLoading(false); }
    };

    const publish = async (id) => {
        setBusy(id);
        try {
            await api.post(`/admin/jobs/${id}/publish`);
            toast.success("Published to public board.");
            setRows((r) => r.filter((j) => j.id !== id));
            if (previewId === id) setPreviewId(null);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(null); }
    };

    const openReject = (id) => { setRejectId(id); setRejectReason(""); };
    const confirmReject = async () => {
        const reason = rejectReason.trim();
        if (reason.length < REJECT_MIN_LEN) {
            toast.error(`Please provide a reason (at least ${REJECT_MIN_LEN} characters).`);
            return;
        }
        setBusy(rejectId);
        try {
            await api.post(`/admin/jobs/${rejectId}/reject`, { reason });
            toast.info("Job rejected. The employer will see your note.");
            setRows((r) => r.filter((j) => j.id !== rejectId));
            setRejectId(null);
            setRejectReason("");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(null); }
    };

    return (
        <div data-testid="admin-job-moderation">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <p className="overline">Command Center</p>
                    <h1 className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">Job Moderation</h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                        Employer-submitted jobs waiting for Simonara approval. Approved jobs go live immediately on the public board and in Google-for-Jobs.
                    </p>
                </div>
                <button onClick={load} disabled={loading} data-testid="jm-refresh" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-60">
                    <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
            </div>

            <div className="mt-6">
                {loading ? (
                    <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
                ) : rows.length === 0 ? (
                    <div data-testid="jm-empty" className="rounded-2xl border border-dashed border-border bg-card/60 p-12 text-center">
                        <Building2 className="mx-auto h-8 w-8 text-muted-foreground" />
                        <h3 className="mt-3 text-base font-semibold text-foreground">No jobs waiting for review.</h3>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {rows.map((j) => (
                            <div key={j.id} data-testid={`jm-row-${j.id}`} className="rounded-2xl border border-border bg-card p-5">
                                <div className="flex flex-wrap justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <h3 className="font-semibold text-foreground">{j.title}</h3>
                                        <p className="text-sm text-muted-foreground mt-0.5">{j.company} · {j.location} · {j.employment_type}</p>
                                        <p className="text-xs text-muted-foreground mt-1">Submitted by <span className="font-medium">{j.submitted_by_email || "—"}</span></p>
                                        {j.description && <p className="text-sm text-muted-foreground mt-2 line-clamp-3">{j.description}</p>}
                                        <button
                                            type="button"
                                            onClick={() => openPreview(j)}
                                            data-testid={`jm-preview-${j.id}`}
                                            className="mt-2 inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400 hover:underline"
                                        >
                                            <Eye size={11} /> Preview full draft
                                        </button>
                                    </div>
                                    <div className="flex gap-2 shrink-0">
                                        <button onClick={() => openReject(j.id)} disabled={busy === j.id} data-testid={`jm-reject-${j.id}`} className="inline-flex items-center gap-1 rounded-full border border-rose-300 bg-white dark:bg-transparent px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-500/10 disabled:opacity-60">
                                            <XCircle className="h-3.5 w-3.5" /> Reject
                                        </button>
                                        <button onClick={() => publish(j.id)} disabled={busy === j.id} data-testid={`jm-publish-${j.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
                                            <CheckCircle2 className="h-3.5 w-3.5" /> Publish
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Preview dialog — admin-only fetch, works even for pending_review */}
            <Dialog open={previewId !== null} onOpenChange={(open) => !open && setPreviewId(null)}>
                <DialogContent data-testid="jm-preview-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Draft preview</DialogTitle>
                        <DialogDescription>
                            Exactly how this posting will appear on the public board once approved.
                        </DialogDescription>
                    </DialogHeader>
                    {previewLoading ? (
                        <div className="py-10 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
                    ) : previewJob ? (
                        <div className="space-y-4 text-sm">
                            <div>
                                <h3 className="font-semibold text-lg text-foreground">{previewJob.title}</h3>
                                <p className="text-xs text-muted-foreground mt-0.5">{previewJob.company}</p>
                            </div>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
                                <span className="inline-flex items-center gap-1"><MapPin size={11} /> {previewJob.location}</span>
                                <Badge variant="outline" className="text-[10px]">{previewJob.employment_type}</Badge>
                                {previewJob.remote_status && previewJob.remote_status !== "on_site" && (
                                    <Badge variant="outline" className="text-[10px] capitalize">{previewJob.remote_status}</Badge>
                                )}
                                {previewJob.industry && (
                                    <span className="inline-flex items-center gap-1"><Briefcase size={11} /> {previewJob.industry}</span>
                                )}
                                {(previewJob.salary_min || previewJob.salary_max) && (
                                    <span className="inline-flex items-center gap-1">
                                        <DollarSign size={11} />
                                        {previewJob.salary_min || "—"}–{previewJob.salary_max || "—"} {previewJob.salary_currency || "USD"}
                                    </span>
                                )}
                            </div>
                            {previewJob.description && (
                                <div>
                                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Description</p>
                                    <p className="whitespace-pre-wrap text-foreground leading-relaxed text-sm">{previewJob.description}</p>
                                </div>
                            )}
                            {Array.isArray(previewJob.benefits) && previewJob.benefits.length > 0 && (
                                <div>
                                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Benefits</p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {previewJob.benefits.map((b, i) => (
                                            <Badge key={i} variant="outline" className="text-[10px]">{b}</Badge>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : null}
                    <DialogFooter className="gap-2">
                        {previewJob && (
                            <>
                                <button
                                    onClick={() => openReject(previewJob.id)}
                                    disabled={busy === previewJob.id}
                                    data-testid="jm-preview-reject"
                                    className="inline-flex items-center gap-1 rounded-full border border-rose-300 bg-white dark:bg-transparent px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-500/10"
                                >
                                    <XCircle className="h-3.5 w-3.5" /> Reject
                                </button>
                                <button
                                    onClick={() => publish(previewJob.id)}
                                    disabled={busy === previewJob.id}
                                    data-testid="jm-preview-publish"
                                    className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
                                >
                                    <CheckCircle2 className="h-3.5 w-3.5" /> Publish
                                </button>
                            </>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reject-with-reason dialog */}
            <Dialog open={rejectId !== null} onOpenChange={(open) => { if (!open) { setRejectId(null); setRejectReason(""); } }}>
                <DialogContent data-testid="jm-reject-dialog">
                    <DialogHeader>
                        <DialogTitle>Reject this job</DialogTitle>
                        <DialogDescription>
                            The employer will see this note in their dashboard. Be specific and constructive — they can edit and resubmit.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Textarea
                            data-testid="jm-reject-textarea"
                            rows={4}
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="e.g., Title should follow 'Level + Role' format; description is missing responsibilities section."
                        />
                        <p className="text-xs text-muted-foreground">Minimum {REJECT_MIN_LEN} characters. Employer sees this note.</p>
                    </div>
                    <DialogFooter className="gap-2">
                        <button
                            type="button"
                            onClick={() => { setRejectId(null); setRejectReason(""); }}
                            data-testid="jm-reject-cancel"
                            className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={confirmReject}
                            disabled={busy === rejectId || rejectReason.trim().length < REJECT_MIN_LEN}
                            data-testid="jm-reject-confirm"
                            className="inline-flex items-center gap-1 rounded-full bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-60"
                        >
                            <XCircle className="h-3.5 w-3.5" /> Reject with note
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
